import { useEffect, useMemo, useState } from 'react';
import { Dropdown, Layout, Typography, theme } from 'antd';
import {
  TeamOutlined,
  AuditOutlined,
  UserOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  BookOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { Settings as LucideSettings } from 'lucide-react';
import { useAuth } from '../stores/AuthContext';
import UsersPage from '@modules/admin/UsersPage';
import AuditLogPage from '@modules/admin/AuditLogPage';
import OwnerDashboardPage from '@modules/dashboard/OwnerDashboardPage';
import SettingsPage from '@modules/settings/SettingsPage';
import ChartOfAccountsPage from '@modules/accounting/ChartOfAccountsPage';
import TrialBalancePage from '@modules/accounting/TrialBalancePage';
import JournalEntriesPage from '@modules/accounting/JournalEntriesPage';
// 12 типізованих XxxPage видалено у Sprint 20+ Refactor Етап 7 UI consolidation:
// Organizations/Counterparties/Currencies/Banks/Nomenclatures/Warehouses/CostItems/
// Contracts/Individuals/Employees/Positions/FixedAssets/IntangibleAssets — усі тепер
// через DynamicCatalogItemsPage(typeCode=...) (див. ACQ_META_MIGRATED_ROUTES).
// Лишилися: ChartOfAccountsPage (tree-view + XLSX), AccountingDirectoriesPage (mixed Tabs).
import DynamicCatalogItemsPage from '@modules/configurator/DynamicCatalogItemsPage';
import DynamicDocumentsPage from '@modules/metadata/DynamicDocumentsPage';
import DocumentTypesPage from '@modules/configurator/DocumentTypesPage';
import SubsystemConstructorPage from '@modules/admin/SubsystemConstructorPage';
import ConfigurationPage from '@modules/configurator/ConfigurationPage';
import ReferencePickerTestPage from '@modules/admin/ReferencePickerTestPage';
import BasImportPage from '@modules/import/BasImportPage';
import BasFormDemoPage from '@modules/metadata/BasFormDemoPage';
import { useDocumentTypes } from '@hooks/useMetadata';
import { useSubsystemTree } from '@hooks/useSubsystems';
import type { MenuProps } from 'antd';
import TopBar from './layout/TopBar';
import CategoryNav from './layout/CategoryNav';
import MegaMenu from './layout/MegaMenu';
import CommandPalette from './layout/CommandPalette';

const { Content } = Layout;

// Префікс для динамічних документ-меню (співпадає з backend doc-type code).
const DOC_KEY_PREFIX = 'doc-type-';

// Префікс для будь-якого meta-каталогу за typeCode (Phase 0d-імпортовані 200+ типів).
// Використовується коли BAS_CATALOG_TO_KEY не має курованої мапи на acc-* route
// (тобто для всіх «нестандартних» каталогів типу Регионы / ХранимыеФайлы / ВидыЦен).
const META_CATALOG_PREFIX = 'meta-catalog:';

/**
 * JS-дзеркало C# BasMetadataParser.ToSnakeCase. Транслітерація кирилиці +
 * camelCase → snake_case. "Регионы" → "regiony", "ХранимыеФайлы" → "khranimye_fajly".
 *
 * ВАЖЛИВО: таблиця транслітерації мусить точно збігатись з C# Transliterate(),
 * інакше клік на BAS-каталог у меню веде на typeCode, якого немає у БД (404).
 * Якщо тут править — синхронно править BasMetadataParser.cs:407-430.
 */
const ACQ_TRANSLIT: Record<string, string> = {
  'А':'A','Б':'B','В':'V','Г':'G','Д':'D','Е':'E','Ё':'E',
  'Ж':'Zh','З':'Z','И':'I','Й':'J','К':'K','Л':'L','М':'M',
  'Н':'N','О':'O','П':'P','Р':'R','С':'S','Т':'T','У':'U',
  'Ф':'F','Х':'Kh','Ц':'Ts','Ч':'Ch','Ш':'Sh','Щ':'Shch',
  'Ъ':'','Ы':'Y','Ь':'','Э':'E','Ю':'Yu','Я':'Ya',
  'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'e',
  'ж':'zh','з':'z','и':'i','й':'j','к':'k','л':'l','м':'m',
  'н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
  'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch',
  'ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya',
  'Ї':'Yi','ї':'yi','Є':'Ye','є':'ye','І':'I','і':'i','Ґ':'G','ґ':'g',
};

function acqBasNameToTypeCode(acqBasName: string): string {
  let acqTr = '';
  for (const ch of acqBasName) acqTr += ACQ_TRANSLIT[ch] ?? ch;
  let acqOut = '';
  for (let i = 0; i < acqTr.length; i++) {
    const ch = acqTr[i];
    if (ch >= 'A' && ch <= 'Z') {
      if (acqOut.length > 0 && acqOut[acqOut.length - 1] !== '_') acqOut += '_';
      acqOut += ch.toLowerCase();
    } else if (/[a-zA-Z0-9]/.test(ch)) {
      acqOut += ch;
    } else {
      if (acqOut.length > 0 && acqOut[acqOut.length - 1] !== '_') acqOut += '_';
    }
  }
  return acqOut.replace(/^_+|_+$/g, '');
}

/**
 * Sprint 20+ Refactor "Domain → Meta-каталоги", Етап 4 інфраструктура.
 *
 * Реєстр route-key → typeCode для довідників, мігрованих на meta. Додавання нового
 * = 1 рядок тут + 1 у CatalogMirrorRegistry.Specs (backend) + qMigratedToMeta set
 * (router) + REFERENCE_TYPES.useUnified=true (для ReferencePicker).
 *
 * acqRenderContent перевіряє цю мапу ДО switch — якщо знайдено, рендериться
 * DynamicCatalogItemsPage(typeCode). Стара типізована сторінка не потрібна.
 */
const ACQ_META_MIGRATED_ROUTES: Record<string, string> = {
  // Етап 4 пілот
  'acc-positions': 'position',
  // Етап 7 — UI consolidation: всі звичайні CRUD-довідники → DynamicCatalogItemsPage
  'acc-organizations': 'organization',
  'acc-counterparties': 'counterparty',
  'acc-nomenclature': 'nomenclature',
  'acc-currencies': 'currency',
  'acc-banks': 'bank',
  'acc-warehouses': 'warehouse',
  'acc-cost-items': 'cost_item',
  'acc-contracts': 'counterparty_contract',
  'acc-individuals': 'individual',
  'acc-employees': 'employee',
  'acc-fixed-assets': 'fixed_asset',
  'acc-intangible-assets': 'intangible_asset',
  // Виключення:
  // - acc-chart (ChartOfAccountsPage) — спеціалізована: tree-view + XLSX import/export
  // - acc-other (AccountingDirectoriesPage) — Tabs з міксом міні-довідників
};

/** BAS-catalog code → frontend route key. Дзеркальна для acqResolveModuleKey. */
const BAS_CATALOG_TO_KEY: Record<string, string> = {
  'Контрагенты': 'acc-counterparties',
  'Организации': 'acc-organizations',
  'Валюты': 'acc-currencies',
  'Банки': 'acc-banks',
  'БанковскиеСчета': 'acc-organizations',
  'Номенклатура': 'acc-nomenclature',
  'НоменклатурныеГруппы': 'acc-nomenclature',
  'Склады': 'acc-warehouses',
  'СтатьиЗатрат': 'acc-cost-items',
  'ДоговорыКонтрагентов': 'acc-contracts',
  'ФизическиеЛица': 'acc-individuals',
  'Сотрудники': 'acc-employees',
  'Должности': 'acc-positions',
  'ОсновныеСредства': 'acc-fixed-assets',
  'НематериальныеАктивы': 'acc-intangible-assets',
  'ПодразделенияОрганизаций': 'acc-organizations',
  'ПланСчетов': 'acc-chart',
};

const BAS_REPORT_TO_KEY: Record<string, string> = {
  'ОборотноСальдоваяВедомость': 'rpt-trial-balance',
  'ОборотноСальдоваяВедомостьПоСчету': 'rpt-trial-balance',
  'ОтчетПоПроводкам': 'rpt-journal',
  'ЖурналПроводок': 'rpt-journal',
  'АнализСчета': 'rpt-trial-balance',
};

/**
 * Резолвить (ItemType, ItemRef) у frontend module-key для switch у acqRenderContent.
 * Повертає null якщо не зможе резолвити (тоді у MegaMenu/CommandPalette — disabled).
 */
function acqResolveModuleKey(acqItemType: number, acqItemRef: string): string | null {
  // Document → завжди doc-type-{code}. Якщо ref — BAS-ім'я (PascalCase кирилицею),
  // транслітеруємо у snake_case через дзеркало C# ToSnakeCase щоб попасти у meta_document_types.Code.
  if (acqItemType === 2) {
    const acqCode = /^[a-z][a-z0-9_]*$/.test(acqItemRef)
      ? acqItemRef // вже snake_case (наприклад, MetaDocumentType створений вручну)
      : acqBasNameToTypeCode(acqItemRef);
    return `${DOC_KEY_PREFIX}${acqCode}`;
  }
  // Catalog
  if (acqItemType === 1) {
    if (acqItemRef.startsWith('acc-')) return acqItemRef;
    // Курована мапа для well-known довідників (двохоп: BAS → acc-* → canonical typeCode).
    // Це зберігає сумісність зі старою типізованою навігацією та з ACQ_META_MIGRATED_ROUTES.
    if (BAS_CATALOG_TO_KEY[acqItemRef]) return BAS_CATALOG_TO_KEY[acqItemRef];
    // Phase 0d fallback: будь-який інший BAS-каталог → meta-catalog:<typeCode>.
    // typeCode виводиться з BAS-name через JS-дзеркало C# ToSnakeCase, тож якщо
    // Phase 0d імпортувано той каталог, MetaCatalogType з тим самим Code існує.
    return `${META_CATALOG_PREFIX}${acqBasNameToTypeCode(acqItemRef)}`;
  }
  // Report
  if (acqItemType === 3) {
    if (acqItemRef.startsWith('rpt-')) return acqItemRef;
    return BAS_REPORT_TO_KEY[acqItemRef] ?? null;
  }
  // DataProcessor — поки не реалізовано
  return null;
}

export default function MainLayout() {
  const [acqCurrentModule, setAcqCurrentModule] = useState('dashboard');
  const [acqOpenCategoryCode, setAcqOpenCategoryCode] = useState<string | null>(null);
  const [acqPaletteOpen, setAcqPaletteOpen] = useState(false);
  const { user } = useAuth();
  const { token: acqTheme } = theme.useToken();
  const acqIsAdmin = user?.roles?.includes('Admin') ?? false;

  const acqDocTypesQuery = useDocumentTypes();
  const acqDocTypes = acqDocTypesQuery.data ?? [];

  const acqSubsystemsQuery = useSubsystemTree(false);
  const acqSubsystems = acqSubsystemsQuery.data ?? [];

  // Hotkey: Ctrl+K / Cmd+K → відкрити CommandPalette
  useEffect(() => {
    const acqHandle = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAcqPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', acqHandle);
    return () => window.removeEventListener('keydown', acqHandle);
  }, []);

  const acqOpenSubsystem = useMemo(
    () => acqSubsystems.find((s) => s.code === acqOpenCategoryCode) ?? null,
    [acqSubsystems, acqOpenCategoryCode]
  );

  const acqHandleTabClick = (acqCode: string) => {
    if (acqCode === 'dashboard') {
      setAcqCurrentModule('dashboard');
      setAcqOpenCategoryCode(null);
      return;
    }
    // Toggle: повторний клік на ту саму вкладку — закриває
    setAcqOpenCategoryCode((v) => (v === acqCode ? null : acqCode));
  };

  const acqSettingsMenu: MenuProps['items'] = [
    { key: 'settings-profile', icon: <UserOutlined />, label: 'Профіль' },
    { type: 'divider' as const },
    // Sprint 20+ Refactor Phase 7d: прибрано submenu «Бухгалтерський облік» + «Кадри / ОЗ».
    // Усі довідники (Контрагенти, Банки, Валюти, ...) тепер через
    // «Конфігурування (дерево) → Довідники → клік на тип» → DynamicCatalogItemsPage.
    // План рахунків — окрема сторінка (tree+XLSX, не unified).
    { key: 'acc-chart', icon: <BookOutlined />, label: 'План рахунків' },
    {
      key: 'sub-reports',
      icon: <BarChartOutlined />,
      label: 'Звіти',
      children: [
        { key: 'rpt-trial-balance', label: 'Оборотно-сальдова відомість' },
        { key: 'rpt-journal', label: 'Журнал проводок' },
      ],
    },
    ...(acqIsAdmin ? [
      { type: 'divider' as const },
      { key: 'settings-configuration', icon: <ExperimentOutlined />, label: 'Панель розробника' },
      { key: 'settings-users', icon: <TeamOutlined />, label: 'Користувачі' },
      { key: 'settings-audit', icon: <AuditOutlined />, label: 'Журнал аудиту' },
      { key: 'settings-constructor', icon: <ExperimentOutlined />, label: 'Конструктор документів' },
      { key: 'settings-subsystems', icon: <AppstoreOutlined />, label: 'Конструктор підсистем' },
      { key: 'settings-refpicker-test', icon: <ExperimentOutlined />, label: 'Тест ReferencePicker' },
      { type: 'divider' as const },
      { key: 'bas-import', icon: <CloudUploadOutlined />, label: 'Імпорт з BAS' },
      { key: 'bas-form-demo', icon: <ExperimentOutlined />, label: 'BAS форма (POC)' },
    ] : []),
  ];

  const acqRenderContent = () => {
    if (acqCurrentModule.startsWith(DOC_KEY_PREFIX)) {
      const acqCode = acqCurrentModule.slice(DOC_KEY_PREFIX.length);
      return <DynamicDocumentsPage typeCode={acqCode} />;
    }

    // Phase 0d fallback: будь-який meta-каталог за typeCode (Регионы, ХранимыеФайлы, ...).
    // Для well-known довідників навігація йде через ACQ_META_MIGRATED_ROUTES (нижче),
    // тут ловимо «довгий хвіст» 200+ типів з BAS-конфігурації.
    if (acqCurrentModule.startsWith(META_CATALOG_PREFIX)) {
      const acqCode = acqCurrentModule.slice(META_CATALOG_PREFIX.length);
      return <DynamicCatalogItemsPage typeCode={acqCode} />;
    }

    // Sprint 20+ Refactor: мігровані довідники рендеримо через DynamicCatalogItemsPage,
    // незалежно від наявності case у switch. Це робить додавання нових 1-line зміною
    // у ACQ_META_MIGRATED_ROUTES без правки switch.
    const acqMigratedTypeCode = ACQ_META_MIGRATED_ROUTES[acqCurrentModule];
    if (acqMigratedTypeCode) return <DynamicCatalogItemsPage typeCode={acqMigratedTypeCode} />;

    switch (acqCurrentModule) {
      case 'dashboard':
        return <OwnerDashboardPage onNavigate={(m) => setAcqCurrentModule(m)} />;
      case 'settings-profile': return <SettingsPage />;
      case 'settings-users': return <UsersPage />;
      case 'settings-audit': return <AuditLogPage />;
      case 'settings-constructor': return <DocumentTypesPage />;
      case 'settings-subsystems': return <SubsystemConstructorPage />;
      case 'settings-configuration':
        return <ConfigurationPage acqOnNavigate={(acqKey) => setAcqCurrentModule(acqKey)} />;
      case 'settings-refpicker-test': return <ReferencePickerTestPage />;
      case 'bas-import': return <BasImportPage />;
      case 'bas-form-demo': return <BasFormDemoPage />;
      case 'acc-chart': return <ChartOfAccountsPage />;
      case 'rpt-trial-balance': return <TrialBalancePage />;
      case 'rpt-journal': return <JournalEntriesPage />;
      // Етап 4 пілот + Етап 7 UI consolidation: 13 типів обробляються через
      // ACQ_META_MIGRATED_ROUTES (вище). Стара типізована XxxPage не потрібна.
      default:
        return (
          <>
            <Typography.Title level={4}>{acqCurrentModule}</Typography.Title>
            <Typography.Text type="secondary">Модуль у розробці або не зарезервовано (resolveItemRef не знайшов відповідної сторінки).</Typography.Text>
          </>
        );
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: acqTheme.colorBgLayout }}>
      <TopBar acqOnSearchClick={() => setAcqPaletteOpen(true)} />

      {/* Wrapper для CategoryNav + MegaMenu з position:relative —
          щоб mega-menu (absolute) накладалось поверх контенту, не зміщуючи його. */}
      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          background: '#fff',
          borderBottom: `1px solid ${acqTheme.colorBorderSecondary}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CategoryNav
              acqSubsystems={acqSubsystems}
              acqActiveCode={acqOpenCategoryCode ?? (acqCurrentModule === 'dashboard' ? 'dashboard' : null)}
              acqOnTabClick={acqHandleTabClick}
            />
          </div>
          {/* Settings dropdown справа */}
          <Dropdown
            menu={{
              items: acqSettingsMenu,
              onClick: ({ key }) => {
                setAcqCurrentModule(key);
                setAcqOpenCategoryCode(null);
              },
            }}
            placement="bottomRight"
          >
            <button
              type="button"
              style={{
                padding: '0 16px',
                height: 38,
                background: 'transparent',
                border: 'none',
                borderLeft: `1px solid ${acqTheme.colorBorderSecondary}`,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
            >
              <LucideSettings size={14} />
              <span>Налаштування</span>
            </button>
          </Dropdown>
        </div>

        {acqOpenSubsystem && (
          <MegaMenu
            acqSubsystem={acqOpenSubsystem}
            acqResolveModuleKey={acqResolveModuleKey}
            acqOnClose={() => setAcqOpenCategoryCode(null)}
            acqOnSelect={(acqKey) => {
              setAcqCurrentModule(acqKey);
              setAcqOpenCategoryCode(null);
            }}
          />
        )}
      </div>

      <Content style={{ margin: 16, padding: 16, background: '#fff', borderRadius: 8 }}>
        {acqRenderContent()}
      </Content>

      <CommandPalette
        acqOpen={acqPaletteOpen}
        acqOnClose={() => setAcqPaletteOpen(false)}
        acqSubsystems={acqSubsystems}
        acqDocTypes={acqDocTypes}
        acqResolveModuleKey={acqResolveModuleKey}
        acqOnSelect={(acqKey) => {
          // CommandPalette повертає або 'subsys-tab-{code}' (відкрити MegaMenu)
          // або реальний module-key (route).
          if (acqKey.startsWith('subsys-tab-')) {
            setAcqOpenCategoryCode(acqKey.slice('subsys-tab-'.length));
          } else {
            setAcqCurrentModule(acqKey);
            setAcqOpenCategoryCode(null);
          }
        }}
      />
    </Layout>
  );
}
